<template>
    <ModalBase :show-modal="true" @close-modal="close" :auto-h="true" :narrow="true" :overlay-scrollb="false" class="add-supplier-modal">
        <template v-slot:titlebar>
            <TitleBar variant-class="title-modal title-modal--simple">
                <template v-slot:title>
                    <span>{{ $gettext('Batch Upload') }}</span>
                </template>
                <template v-slot:subtitle>
                    <span class="text-unassigned">{{ $gettext('You can upload multiple costs at once by attaching your PDF or image files.') }}</span>
                </template>
            </TitleBar>
        </template>

        <template v-slot:maincontent>
            <div v-if="!isUploading">
                <div class="d-flex flex-column flex-lg-row align-items-stretch mass-upload-layout">
                    <div class="mass-upload-panel-left flex-fill">
                        <div
                            class="dropzone h-100"
                            :class="{ 'dropzone--active': isDragActive }"
                            @click="openFilePicker"
                            @dragenter.prevent="setDragActive(true)"
                            @dragover.prevent="setDragActive(true)"
                            @dragleave.prevent="handleDragLeave"
                            @drop.prevent="handleDrop"
                        >
                            <div class="text-center py-5 px-4 h-100 d-flex flex-column justify-content-center">
                                <div class="dropzone-icon mb-3">
                                    <i class="fa-solid fa-cloud-arrow-up"></i>
                                </div>
                                <h6 class="mb-2">{{ $gettext('Drag and drop your files here') }}</h6>
                                <p class="text-muted mb-3">
                                    {{ $gettext('Or click to browse PDF and image files.') }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <input
                        ref="fileInput"
                        type="file"
                        class="d-none"
                        multiple
                        accept=".pdf,image/*"
                        @change="handleFileSelection"
                    >

                    <div class="mass-upload-panel-right flex-fill">
                        <div class="uploaded-files-card h-100">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <strong>{{ $gettext('Selected files') }}</strong>
                                <span class="badge bg-light text-dark border">{{ uploadedFiles.length }}/25</span>
                            </div>

                            <div v-if="uploadedFiles.length" class="uploaded-files-list">
                                <ul class="list-group list-group-flush">
                                    <li
                                        v-for="(file, index) in uploadedFiles"
                                        :key="`${file.name}-${file.size}-${index}`"
                                        class="list-group-item px-0 d-flex justify-content-between align-items-center"
                                    >
                                        <div class="me-3 text-truncate">
                                            <div class="fw-semibold text-truncate">{{ file.name }}</div>
                                            <small class="text-muted">{{ formatFileSize(file.size) }}</small>
                                        </div>
                                        <GekkoButton
                                            class="text-danger"
                                            variant="link"
                                            icon="fa-solid fa-trash"
                                            @click.stop="removeFile(index)"
                                        />
                                    </li>
                                </ul>
                            </div>

                            <div v-else>
                                <div class="empty-uploaded-files d-flex flex-column justify-content-center align-items-center text-center">
                                    <div class="fw-semibold mb-1">{{ $gettext('No files uploaded yet') }}</div>
                                    <div class="text-muted small">
                                        {{ $gettext('Your selected PDF and image files will appear here.') }}
                                    </div>
                                </div>
                                <div class="small text-muted mt-3">
                                    {{ $gettext('Maximum 25 files.') }}
                                </div>
                                <div class="small text-muted">
                                    {{ $gettext('Maximum size per file: 5 MB.') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="uploadError" class="alert alert-danger mt-3 mb-0" role="alert">
                    {{ uploadError }}
                </div>
            </div>

            <div v-if="isUploading" class="upload-progress">
                <div class="upload-progress__body py-4 px-4">
                    <div :class="['upload-progress__bar mb-2', { 'upload-progress__bar--active': isUploading }]">
                        <b-progress
                            :value="uploadProgressPercentage"
                            :max="100"
                            variant="info"
                        ></b-progress>
                    </div>

                    <div v-if="!isUploadingComplete" class="d-flex justify-content-between align-items-center small text-muted">
                        <span>{{ $gettext('Uploading files... Please don\'t close the window.') }}</span>
                        <span>{{ completedUploads }}/{{ totalUploads }}</span>
                    </div>

                    <div v-if="isUploadingComplete" class="upload-progress__success d-inline-flex align-items-center">
                        <div class="upload-progress__success-animation">
                            <AnimationItem :animation="{ animationData: successAnimation.default, loop: false }" :key="'anim_success'" />
                        </div>
                        <span class="small text-muted text-nowrap">{{ $gettext('Upload complete!') }}</span>
                    </div>
                </div>
            </div>
        </template>
        
        <template v-slot:actions>
            <GekkoButton
                @click="upload()"
                icon="fa-solid fa-upload"
                :text="$gettext('Upload')"
                :disabled="isUploading || !uploadedFiles.length"
                variant="info"
                class="pull-right" />
        </template>
    </ModalBase>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import ModalBase from '@/components/base_components/ModalBase'
import TitleBar from '@/components/TitleBar'
import GekkoButton from '@/components/GekkoButton'
import AiAssistantService from '@/store/services/ai.assistant.service'
import * as successAnimation from '@/css/animations/success-check.json';
import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'

export default {
    name: "MassUploadCostsModal",
    components: {
        ModalBase,
        TitleBar,
        GekkoButton,
        AnimationItem
    },
    filters: {
        formatCount(value) {
            return value
        }
    },
    data () {
        return {
            isUploading: false,
            isUploadingComplete: false,
            isDragActive: false,
            uploadError: '',
            uploadedFiles: [],
            completedUploads: 0,
            totalUploads: 0,
            maxFiles: 25,
            maxFileSizeBytes: 5 * 1024 * 1024,
            successAnimation: successAnimation,
            newSuppliers: []
        }
    },
    computed:{
        ...mapGetters({
            getCompanyId: 'main/getCompanyId'
        }),
        uploadProgressPercentage() {
            if (!this.totalUploads) {
                return 0
            }

            const percentage = Math.round((this.completedUploads / this.totalUploads) * 100)
            
            if (percentage <= 0) {
                return (100 / this.maxFiles) / 2 // Show a small progress for the first file to indicate that the upload has started
            }

            return percentage
        }
    },
    methods: {
        ...mapMutations({
            togglePremiumModal: 'togglePremiumModal'
        }),
        close() {
            this.$emit('close')
        },
        openFilePicker() {
            this.$refs.fileInput.click()
        },
        setDragActive(value) {
            this.isDragActive = value
        },
        handleDragLeave(event) {
            if (event.currentTarget.contains(event.relatedTarget)) {
                return
            }

            this.setDragActive(false)
        },
        handleDrop(event) {
            this.setDragActive(false)
            this.addFiles(event.dataTransfer.files)
        },
        handleFileSelection(event) {
            this.addFiles(event.target.files)
            event.target.value = ''
        },
        addFiles(fileList) {
            this.uploadError = ''

            const incomingFiles = Array.from(fileList || [])
            if (!incomingFiles.length) {
                return
            }

            const oversizedFile = incomingFiles.find(file => file.size > this.maxFileSizeBytes)
            if (oversizedFile) {
                this.uploadError = this.$gettext('Each file must be 5 MB or smaller.')
                return
            }

            const availableSlots = this.maxFiles - this.uploadedFiles.length
            if (availableSlots <= 0) {
                this.uploadError = this.$gettext('You can upload a maximum of 25 files.')
                return
            }

            if (incomingFiles.length > availableSlots) {
                this.uploadError = this.$gettext('You can upload a maximum of 25 files.')
            }

            this.uploadedFiles = [
                ...this.uploadedFiles,
                ...incomingFiles.slice(0, availableSlots)
            ]
        },
        removeFile(index) {
            this.uploadedFiles.splice(index, 1)
            this.uploadError = ''
        },
        formatFileSize(size) {
            const sizeInMb = size / (1024 * 1024)
            return `${sizeInMb.toFixed(2)} MB`
        },
        getUploadFailureMessage(error) {
            if (error && error.status === 422) {
                return error.data?.detail || this.$gettext('Some of the files you uploaded were not recognized as valid cost documents. Please make sure the files are in the correct format.')
            }

            return this.$gettext('Some files could not be uploaded. Please try again.')
        },
        async upload() {
            if (this.isUploading || !this.uploadedFiles.length) {
                return
            }

            this.isUploading = true
            this.uploadError = ''
            this.completedUploads = 0

            const filesToUpload = [...this.uploadedFiles]
            this.totalUploads = filesToUpload.length
            const failedIndexes = []
            let uploadFailureMessage = ''

            for (const [index, file] of filesToUpload.entries()) {
                try {
                    const response = await AiAssistantService.scanCostImage(this.getCompanyId, file)
                    if (response.data.new_supplier) {
                        this.newSuppliers.push(response.data)
                    }
                    this.$store.commit('costs/REFRESH_COST_LIST')
                } catch (error) {
                    if (error.status === 402) {
                        this.$emit('close')
                        this.togglePremiumModal(true)
                        return
                    } else if (error.status === 422) {
                        uploadFailureMessage = this.getUploadFailureMessage(error)
                        failedIndexes.push(index)
                    } else {
                        uploadFailureMessage = uploadFailureMessage || this.getUploadFailureMessage(error)
                        failedIndexes.push(index)
                    }
                } finally {
                    this.completedUploads += 1
                }
            }

            this.isUploadingComplete = true
            setTimeout(() => {
                this.isUploading = false
                this.$emit('upload-finished')
                if (failedIndexes.length) {
                    this.uploadError = uploadFailureMessage || this.$gettext('Some files could not be uploaded. Please try again.')
                    this.uploadedFiles = filesToUpload.filter((file, index) => failedIndexes.includes(index))
                    this.isUploadingComplete = false
                    return
                }

                if (this.newSuppliers.length) {
                    this.$emit('new-suppliers-detected', this.newSuppliers)
                }
                
                this.uploadedFiles = []
                this.$store.commit('costs/REFRESH_COST_LIST')
                this.$emit('close')
            }, 1500) // Add a small delay to allow users to see the completed progress bar before the modal closes
        }
    }

}
</script>

<style lang="scss" scoped>
.mass-upload-layout {
    min-height: 20rem;
    gap: 1rem;
}

.mass-upload-panel-left {
    flex: 1 1 100%;
    min-width: 0;
}

.mass-upload-panel-right {
    flex: 1 1 100%;
    min-width: 0;
}

.dropzone {
    cursor: pointer;
    border: 1px dashed #cbd5e1;
    border-radius: 1rem;
    background: linear-gradient(180deg, #fbfdff 0%, #f7fafc 100%);
    transition: border-color 0.2s ease, transform 0.2s ease, background-color 0.2s ease;
}

.dropzone:hover,
.dropzone--active {
    border-color: #7aa7e8;
    transform: translateY(-1px);
    background: linear-gradient(180deg, #f8fbff 0%, #f1f7ff 100%);
}

.dropzone-icon {
    width: 4rem;
    height: 4rem;
    margin: 0 auto;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: #5f8fd6;
    background: #eaf2ff;
}

.uploaded-files-card {
    height: 100%;
    padding: 1.25rem;
    border: 1px solid #e2e8f0;
    border-radius: 1rem;
    background: #ffffff;
}

.list-group-item {
    background: transparent;
    border-color: #edf2f7;
}

.uploaded-files-list {
    width: 100%;
    min-height: 16rem;
    padding: 0.25rem 1rem;
    border: 1px dashed #dbe4ee;
    border-radius: 0.75rem;
    background: #fafcfe;
    max-height: 16rem;
    overflow-y: auto;
    overflow-x: hidden;
}

.empty-uploaded-files {
    min-height: 16rem;
    padding: 1rem;
    border: 1px dashed #dbe4ee;
    border-radius: 0.75rem;
    background: #fafcfe;
}

.upload-progress {
    margin-top: 0.25rem;
    border: 1px solid #e2e8f0;
    border-radius: 1rem;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.upload-progress__body {
    border-radius: inherit;
}

.upload-progress__success {
    width: auto;
    max-width: none;
    gap: 0.5rem;
}

.upload-progress__success-animation {
    width: 30px;
    height: 30px;
    flex: 0 0 auto;
    overflow: hidden;
}

.upload-progress__success-animation ::v-deep > * {
    width: 100%;
    height: 100%;
}

.upload-progress__success-animation ::v-deep svg,
.upload-progress__success-animation ::v-deep canvas,
.upload-progress__success-animation ::v-deep div {
    width: 100% !important;
    height: 100% !important;
    max-width: 100%;
    max-height: 100%;
    margin: 0;
}

.upload-progress__bar ::v-deep .progress {
    height: 0.75rem;
    border-radius: 999px;
    background: #edf2f7;
}

.upload-progress__bar ::v-deep .progress-bar {
    position: relative;
    overflow: hidden;
    background: linear-gradient(90deg, #6ea8fe 0%, #4f8df7 100%);
    transition: width 0.45s ease-in-out, background-position 0.8s ease;
    background-size: 200% 100%;
}

.upload-progress__bar--active ::v-deep .progress-bar {
    animation: upload-progress-flow 2.4s ease-in-out infinite;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 0 10px rgba(79, 141, 247, 0.22);
}

.upload-progress__bar--active ::v-deep .progress-bar::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(115deg, rgba(255, 255, 255, 0) 15%, rgba(255, 255, 255, 0.28) 45%, rgba(255, 255, 255, 0.12) 60%, rgba(255, 255, 255, 0) 85%);
    transform: translateX(-100%);
    animation: upload-progress-shade 2.1s ease-in-out infinite;
}

@keyframes upload-progress-flow {
    0% {
        background-position: 0% 50%;
    }

    50% {
        background-position: 100% 50%;
    }

    100% {
        background-position: 0% 50%;
    }
}

@keyframes upload-progress-shade {
    0% {
        transform: translateX(-115%);
        opacity: 0;
    }

    20% {
        opacity: 1;
    }

    70% {
        opacity: 1;
    }

    100% {
        transform: translateX(115%);
        opacity: 0;
    }
}

@media (min-width: 992px) {
    .mass-upload-panel-left {
        flex-basis: 45%;
        max-width: 45%;
    }

    .mass-upload-panel-right {
        flex-basis: 55%;
        max-width: 55%;
    }
}
</style>